// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.

// Shared geometry math for clade-annotation and leaf-label-annotation SVG
// renderers. All coordinates are in node.x/node.y pixel space — the same
// space as edges and shapes, rendered inside the translated root <g>.
//
// Exports:
//   getLeafDescendants        — DFS leaf collector
//   estimateTextWidth         — character-width heuristic
//   getPolygonBounds          — bounding box of an [x,y][] polygon
//   computeConvexHull         — Andrew's monotone chain
//   chaikinSmooth             — 1/4:3/4 corner cutting
//   catmullRomSpline          — closed Catmull-Rom interpolation
//   buildRadialHullPolygon    — full radial convex-hull pipeline
//   createWedgePolygon        — sampled annular sector polygon
//   buildAnnularSectorPath    — SVG path string for ring sector
//   calculateLabelPosition    — label [x,y] from polygon + textPosition
//   calculateWedgeLabelPosition — label [x,y] for wedge-shaped annotations

// ---------------------------------------------------------------------------
// Leaf traversal
// ---------------------------------------------------------------------------

export function getLeafDescendants(node) {
  const leaves = [];

  function traverse(n) {
    if (n.isLeaf) {
      leaves.push(n);
    } else if (n.children) {
      n.children.forEach(traverse);
    }
  }

  traverse(node);
  return leaves;
}

// ---------------------------------------------------------------------------
// Text measurement
// ---------------------------------------------------------------------------

export function estimateTextWidth(text, fontSize) {
  return (text || '').length * (fontSize * 0.55);
}

// ---------------------------------------------------------------------------
// Polygon utilities
// ---------------------------------------------------------------------------

export function getPolygonBounds(polygon) {
  const xs = polygon.map(p => p[0]);
  const ys = polygon.map(p => p[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function getPolygonCenter(polygon) {
  const sumX = polygon.reduce((sum, p) => sum + p[0], 0);
  const sumY = polygon.reduce((sum, p) => sum + p[1], 0);
  return [sumX / polygon.length, sumY / polygon.length];
}

// ---------------------------------------------------------------------------
// Convex hull — Andrew's monotone chain
// ---------------------------------------------------------------------------

export function computeConvexHull(points) {
  if (points.length < 3) {
    return points.slice();
  }

  const sorted = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  const cross = (o, a, b) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

// ---------------------------------------------------------------------------
// Chaikin smoothing
// ---------------------------------------------------------------------------

export function chaikinSmooth(polygon, iterations = 3) {
  let result = polygon.slice();

  for (let iter = 0; iter < iterations; iter++) {
    const smoothed = [];
    const n = result.length;

    for (let i = 0; i < n; i++) {
      const p0 = result[i];
      const p1 = result[(i + 1) % n];

      const q = [
        0.75 * p0[0] + 0.25 * p1[0],
        0.75 * p0[1] + 0.25 * p1[1],
      ];
      const r = [
        0.25 * p0[0] + 0.75 * p1[0],
        0.25 * p0[1] + 0.75 * p1[1],
      ];

      smoothed.push(q, r);
    }

    result = smoothed;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Catmull-Rom spline
// ---------------------------------------------------------------------------

function catmullRomPoint(p0, p1, p2, p3, t, tension) {
  const t2 = t * t;
  const t3 = t2 * t;

  const alpha = tension;

  const m1x = alpha * (p2[0] - p0[0]);
  const m1y = alpha * (p2[1] - p0[1]);
  const m2x = alpha * (p3[0] - p1[0]);
  const m2y = alpha * (p3[1] - p1[1]);

  const a0 = 2 * t3 - 3 * t2 + 1;
  const a1 = t3 - 2 * t2 + t;
  const a2 = -2 * t3 + 3 * t2;
  const a3 = t3 - t2;

  return [
    a0 * p1[0] + a1 * m1x + a2 * p2[0] + a3 * m2x,
    a0 * p1[1] + a1 * m1y + a2 * p2[1] + a3 * m2y,
  ];
}

export function catmullRomSpline(polygon, pointsPerSegment = 12, tension = 0.5) {
  if (polygon.length < 3) {
    return polygon.slice();
  }

  const result = [];
  const n = polygon.length;

  for (let i = 0; i < n; i++) {
    const p0 = polygon[(i - 1 + n) % n];
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % n];
    const p3 = polygon[(i + 2) % n];

    for (let j = 0; j < pointsPerSegment; j++) {
      const t = j / pointsPerSegment;
      const point = catmullRomPoint(p0, p1, p2, p3, t, tension);
      result.push(point);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Hull expansion helpers (used internally by buildRadialHullPolygon)
// ---------------------------------------------------------------------------

function normalize(vec) {
  const len = Math.sqrt(vec[0] * vec[0] + vec[1] * vec[1]);
  if (len === 0) return [0, 0];
  return [vec[0] / len, vec[1] / len];
}

function expandHullByNormals(hull, buffer) {
  if (hull.length < 3) return hull;

  const n = hull.length;
  const expanded = [];

  const cx = hull.reduce((s, p) => s + p[0], 0) / n;
  const cy = hull.reduce((s, p) => s + p[1], 0) / n;

  for (let i = 0; i < n; i++) {
    const prev = hull[(i - 1 + n) % n];
    const curr = hull[i];
    const next = hull[(i + 1) % n];

    const edge1 = [curr[0] - prev[0], curr[1] - prev[1]];
    const edge2 = [next[0] - curr[0], next[1] - curr[1]];

    const normal1 = normalize([edge1[1], -edge1[0]]);
    const normal2 = normalize([edge2[1], -edge2[0]]);

    let avgNormal = normalize([
      normal1[0] + normal2[0],
      normal1[1] + normal2[1],
    ]);

    if (avgNormal[0] === 0 && avgNormal[1] === 0) {
      avgNormal = normal1;
    }

    const toCenter = [cx - curr[0], cy - curr[1]];
    const dot = avgNormal[0] * toCenter[0] + avgNormal[1] * toCenter[1];

    const outwardNormal = dot > 0
      ? [-avgNormal[0], -avgNormal[1]]
      : avgNormal;

    expanded.push([
      curr[0] + buffer * outwardNormal[0],
      curr[1] + buffer * outwardNormal[1],
    ]);
  }

  return expanded;
}

function addInternalBranchPoints(node, points) {
  function traverse(n) {
    if (!n.children) return;

    for (const child of n.children) {
      points.push([child.x, child.y]);
      points.push([
        (n.x + child.x) / 2,
        (n.y + child.y) / 2,
      ]);
      traverse(child);
    }
  }

  traverse(node);
}

// ---------------------------------------------------------------------------
// Radial hull pipeline
// ---------------------------------------------------------------------------

export function buildRadialHullPolygon(node, leaves, outerRadius, extendToLabels) {
  const buffer = 15;
  const points = [];

  points.push([node.x, node.y]);

  for (const leaf of leaves) {
    let leafX, leafY;
    if (extendToLabels) {
      leafX = outerRadius * Math.cos(leaf.angle);
      leafY = outerRadius * Math.sin(leaf.angle);
    } else {
      leafX = leaf.x;
      leafY = leaf.y;
    }

    points.push([leafX, leafY]);

    const steps = 3;
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      points.push([
        node.x + (leafX - node.x) * t,
        node.y + (leafY - node.y) * t,
      ]);
    }
  }

  addInternalBranchPoints(node, points);

  const hull = computeConvexHull(points);
  const expandedHull = expandHullByNormals(hull, buffer);
  const softenedHull = chaikinSmooth(expandedHull, 2);
  const smoothedHull = catmullRomSpline(softenedHull, 6, 0.3);

  return smoothedHull;
}

// ---------------------------------------------------------------------------
// Wedge polygon (sampled, for fill rendering)
// ---------------------------------------------------------------------------

export function createWedgePolygon(startAngle, endAngle, innerRadius, outerRadius, segments = 50) {
  const polygon = [];

  for (let i = 0; i <= segments; i++) {
    const angle = startAngle + (endAngle - startAngle) * (i / segments);
    polygon.push([
      outerRadius * Math.cos(angle),
      outerRadius * Math.sin(angle),
    ]);
  }

  for (let i = segments; i >= 0; i--) {
    const angle = startAngle + (endAngle - startAngle) * (i / segments);
    polygon.push([
      innerRadius * Math.cos(angle),
      innerRadius * Math.sin(angle),
    ]);
  }

  return polygon;
}

// ---------------------------------------------------------------------------
// Annular sector SVG path
//
// Arc winding convention: sweep-flag=1 on the outer arc (startAngle→endAngle,
// clockwise in SVG's y-down space, i.e. increasing angle), sweep-flag=0 on the
// inner arc (endAngle→startAngle, counter-clockwise). This matches edges.js
// describeArc which uses sweep-flag=0 ("A radius radius 0 largeArcFlag 0 …")
// to travel from the point at endAngle back toward startAngle — the same
// counter-clockwise / decreasing-angle direction used for the inner return arc.
// ---------------------------------------------------------------------------

export function buildAnnularSectorPath(startAngle, endAngle, innerRadius, outerRadius) {
  const largeArcFlag = (endAngle - startAngle) > Math.PI ? 1 : 0;

  const outerStart = [outerRadius * Math.cos(startAngle), outerRadius * Math.sin(startAngle)];
  const outerEnd   = [outerRadius * Math.cos(endAngle),   outerRadius * Math.sin(endAngle)];
  const innerEnd   = [innerRadius * Math.cos(endAngle),   innerRadius * Math.sin(endAngle)];
  const innerStart = [innerRadius * Math.cos(startAngle), innerRadius * Math.sin(startAngle)];

  return [
    `M ${outerStart[0]} ${outerStart[1]}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd[0]} ${outerEnd[1]}`,
    `L ${innerEnd[0]} ${innerEnd[1]}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart[0]} ${innerStart[1]}`,
    "Z",
  ].join(" ");
}

// ---------------------------------------------------------------------------
// Label positioning
// ---------------------------------------------------------------------------

export function calculateLabelPosition(polygon, textPosition = 'center', fontSize = 16) {
  const bounds = getPolygonBounds(polygon);
  const center = getPolygonCenter(polygon);
  const padding = fontSize * 0.5;

  switch (textPosition) {
    case 'center':
      return center;

    case 'top-left':
      return [bounds.minX + padding, bounds.minY + padding];

    case 'top-right':
      return [bounds.maxX - padding, bounds.minY + padding];

    case 'bottom-left':
      return [bounds.minX + padding, bounds.maxY - padding];

    case 'bottom-right':
      return [bounds.maxX - padding, bounds.maxY - padding];

    case 'top-outer':
      return [center[0], bounds.minY - padding * 2];

    case 'bottom-outer':
      return [center[0], bounds.maxY + padding * 2];

    case 'left-outer':
      return [bounds.minX - padding * 2, center[1]];

    case 'right-outer':
      return [bounds.maxX + padding * 2, center[1]];

    default:
      return center;
  }
}

export function calculateWedgeLabelPosition(minAngle, maxAngle, innerRadius, outerRadius, textPosition = 'center', fontSize = 16) {
  const midAngle = (minAngle + maxAngle) / 2;
  const midRadius = (innerRadius + outerRadius) / 2;
  const padding = fontSize * 0.5;

  switch (textPosition) {
    case 'center':
      return [
        midRadius * Math.cos(midAngle),
        midRadius * Math.sin(midAngle)
      ];

    case 'top-left':
      return [
        (innerRadius + padding) * Math.cos(minAngle + 0.1),
        (innerRadius + padding) * Math.sin(minAngle + 0.1)
      ];

    case 'top-right':
      return [
        (outerRadius - padding) * Math.cos(minAngle + 0.1),
        (outerRadius - padding) * Math.sin(minAngle + 0.1)
      ];

    case 'bottom-left':
      return [
        (innerRadius + padding) * Math.cos(maxAngle - 0.1),
        (innerRadius + padding) * Math.sin(maxAngle - 0.1)
      ];

    case 'bottom-right':
      return [
        (outerRadius - padding) * Math.cos(maxAngle - 0.1),
        (outerRadius - padding) * Math.sin(maxAngle - 0.1)
      ];

    case 'top-outer':
      return [
        (outerRadius + padding * 2) * Math.cos(minAngle),
        (outerRadius + padding * 2) * Math.sin(minAngle)
      ];

    case 'bottom-outer':
      return [
        (outerRadius + padding * 2) * Math.cos(maxAngle),
        (outerRadius + padding * 2) * Math.sin(maxAngle)
      ];

    case 'left-outer':
      return [
        (innerRadius - padding * 2) * Math.cos(midAngle),
        (innerRadius - padding * 2) * Math.sin(midAngle)
      ];

    case 'right-outer':
      return [
        (outerRadius + padding * 2) * Math.cos(midAngle),
        (outerRadius + padding * 2) * Math.sin(midAngle)
      ];

    default:
      return [
        midRadius * Math.cos(midAngle),
        midRadius * Math.sin(midAngle)
      ];
  }
}
